import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Doughnut, Bar } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from "chart.js";
import { statsAPI, familyAPI } from "../api";
import useAuthStore from "../store/auth";
import { formatAmount } from "../utils/currency";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function StatCard({ icon, label, value, color, border }) {
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderTop: "3px solid " + border, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 11, color: "var(--text2)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 700, color, wordBreak: "break-all" }}>{value}</div>
    </div>
  );
}

function PersonalDashboard({ user, fmt, t, navigate, month }) {
  const { data: dash, isLoading } = useQuery({
    queryKey: ["dashboard", month],
    queryFn: () => statsAPI.dashboard({ month }).then(r => r.data),
  });
  const { data: trend } = useQuery({
    queryKey: ["trend"],
    queryFn: () => statsAPI.trend().then(r => r.data),
  });

  if (isLoading) return <p style={{ color: "var(--text2)", padding: 40, textAlign: "center" }}>{t("loading")}</p>;

  const cats = (dash?.by_category || []).filter(c => parseFloat(c.total) > 0);
  const chartOpts = {
    responsive: true,
    plugins: { legend: { labels: { color: "#9090b0", boxWidth: 12 } } },
    scales: { x: { grid: { color: "#2a2a3d" }, ticks: { color: "#9090b0", maxRotation: 45 } }, y: { grid: { color: "#2a2a3d" }, ticks: { color: "#9090b0", callback: v => fmt(v) } } },
  };

  return (
    <div>
      <div className="stats-grid">
        <StatCard icon="💵" label={t("total_income")}  value={fmt(dash?.total_income)}  color="var(--accent3)" border="#6af7b8" />
        <StatCard icon="💸" label={t("total_expense")} value={fmt(dash?.total_expense)} color="var(--danger)"   border="#f76a6a" />
        <StatCard icon="💰" label={t("balance")}       value={fmt(dash?.balance)}       color="var(--accent)"  border="#7c6af7" />
        <StatCard icon="🎯" label={t("savings")}       value={fmt(dash?.savings)}       color="var(--accent2)" border="#f7c26a" />
      </div>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 20 }}>
          <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, marginBottom: 16, fontSize: 15 }}>{t("income")} vs {t("expenses")}</h3>
          {trend?.length > 0
            ? <Bar data={{ labels: trend.map(m => m.month), datasets: [
                { label: t("income"),   data: trend.map(m => m.income),  backgroundColor: "#6af7b840", borderColor: "#6af7b8", borderWidth: 2, borderRadius: 4 },
                { label: t("expenses"), data: trend.map(m => m.expense), backgroundColor: "#f76a6a40", borderColor: "#f76a6a", borderWidth: 2, borderRadius: 4 },
              ]}} options={chartOpts} />
            : <p style={{ color: "var(--text3)", textAlign: "center", padding: 30 }}>{t("no_data")}</p>}
        </div>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 20 }}>
          <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, marginBottom: 16, fontSize: 15 }}>{t("by_category")}</h3>
          {cats.length > 0
            ? <Doughnut data={{ labels: cats.map(c => c.name || "Autre"), datasets: [{ data: cats.map(c => c.total), backgroundColor: cats.map(c => (c.color || "#7c6af7") + "cc"), borderColor: cats.map(c => c.color || "#7c6af7"), borderWidth: 2 }]}}
                options={{ responsive: true, cutout: "60%", plugins: { legend: { position: "bottom", labels: { color: "#9090b0", padding: 8, boxWidth: 12 } } } }} />
            : <p style={{ color: "var(--text3)", textAlign: "center", padding: 30 }}>{t("no_data")}</p>}
        </div>
      </div>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 20 }}>
        <h3 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, marginBottom: 16, fontSize: 15 }}>{t("recent_transactions")}</h3>
        {!(dash?.recent?.length) ? <p style={{ color: "var(--text3)", textAlign: "center", padding: 30 }}>{t("no_data")}</p> : (
          <div className="table-wrap">
            <table className="resp-table table-stack" style={{ width: "100%" }}>
              <thead><tr>
                {[t("date"), t("description"), t("category"), t("amount")].map(h => <th key={h}>{h}</th>)}
              </tr></thead>
              <tbody>
                {dash.recent.map((item, i) => (
                  <tr key={i}>
                    <td data-label={t("date")} style={{ color: "var(--text2)", fontSize: 13 }}>{new Date(item.date).toLocaleDateString("fr-FR")}</td>
                    <td data-label={t("description")} style={{ fontSize: 13 }}>{item.description || item.label || "-"}</td>
                    <td data-label={t("category")}>
                      <span style={{ padding: "3px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: item.type === "income" ? "#6af7b820" : "#f76a6a20", color: item.type === "income" ? "var(--accent3)" : "var(--danger)" }}>
                        {item.type === "income" ? t("income") : t("expenses")}
                      </span>
                    </td>
                    <td data-label={t("amount")} style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, color: item.type === "income" ? "var(--accent3)" : "var(--danger)" }}>
                      {item.type === "income" ? "+" : "-"}{fmt(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const currency = user?.currency || "EUR";
  const fmt = n => formatAmount(n, currency);
  const navigate = useNavigate();
  const isFamily = user?.account_type === "family";
  const [view, setView]   = useState(isFamily ? "family" : "personal");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: "Syne,sans-serif", fontWeight: 700 }}>{t("dashboard")}</h1>
          <p style={{ color: "var(--text2)", fontSize: 14 }}>{t("hello")}, <strong>{user?.name}</strong> 👋</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", color: "var(--text)", fontSize: 14, width: "auto" }} />
          {isFamily && (
            <div style={{ display: "flex", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 3, gap: 3 }}>
              <button onClick={() => setView("personal")}
                style={{ padding: "7px 12px", borderRadius: 7, border: "none", background: view === "personal" ? "var(--accent)" : "transparent", color: view === "personal" ? "#fff" : "var(--text2)", fontSize: 12, fontWeight: 600, cursor: "pointer", minHeight: "auto" }}>
                👤
              </button>
              <button onClick={() => setView("family")}
                style={{ padding: "7px 12px", borderRadius: 7, border: "none", background: view === "family" ? "var(--accent)" : "transparent", color: view === "family" ? "#fff" : "var(--text2)", fontSize: 12, fontWeight: 600, cursor: "pointer", minHeight: "auto" }}>
                👨‍👩‍👧
              </button>
            </div>
          )}
          <button onClick={() => navigate("/income")} style={{ padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, color: "var(--text)", fontSize: 13, cursor: "pointer", minHeight: "auto" }}>+ {t("income")}</button>
          <button onClick={() => navigate("/expenses")} style={{ padding: "8px 14px", background: "var(--accent)", border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", minHeight: "auto" }}>+ {t("expenses")}</button>
        </div>
      </div>
      <PersonalDashboard user={user} fmt={fmt} t={t} navigate={navigate} month={month} />
    </div>
  );
}